import { Box, Flex, Image, Text } from "@chakra-ui/react";
import { Article, Artist, Comic, Release } from "../../../../types";
import { SearchResponse } from "../../api/globalSearchApi";
import { useNavigate } from "react-router-dom";
import { BrowseEmptyState } from "../../../../screens/browse/components/EmptyState";
import { useNavigateToReaderWithContext } from "../../../../screens/reader/hooks/useNavigateToReaderWithContext";
import { NavigationTrigger } from "../../../../screens/reader/types";
import { useSlideInMenu } from "../../../slideInMenu/hooks/useSlideInMenu";

type ResultDisplay = {
  image: string;
  title: string;
  url: string;
  type: string;
  entity: (Artist | Article | Release | Comic) & { artist_roman_name?: string };
};

export function SearchResults({
  results,
  searchTerm,
  onNavigate,
}: {
  results: SearchResponse;
  searchTerm: string;
  onNavigate?: () => void;
}) {
  // Join results together
  const baseResults = [
    ...results.comics,
    ...results.releases,
    ...results.articles,
    ...results.artists,
  ];
  const combinedResults: ResultDisplay[] = baseResults.map(
    (result: Article | Artist | Comic | Release) => {
      const image = (() => {
        switch (result.entity_type) {
          case "Artist":
            return result.avatar_url;
          case "Comic":
            return result.image_small_url;
          case "Release":
            return result.thumbnail_url;
          case "News":
            return result.preview_image_url;
        }
      })();
      const title = (() => {
        switch (result.entity_type) {
          case "Artist":
            return result.roman_name;
          case "Comic":
            return result.name;
          case "Release":
            return result.localized_name;
          case "News":
            return result.title;
        }
      })();
      const url = result.url;
      return {
        image,
        title,
        url,
        type: result.entity_type,
        entity: result,
      };
    },
  );

  // Boost first artist/publisher that starts with search term (only if they have 50+ favorites)
  if (searchTerm.length >= 2) {
    const searchLower = searchTerm.toLowerCase();

    // Find the first artist that starts with the search term and has enough followers
    const artistIndex = combinedResults.findIndex(
      (result) =>
        result.type === "Artist" &&
        result.title.toLowerCase().startsWith(searchLower) &&
        (result.entity as Artist).total_favorites >= 50,
    );

    // If found and not already first, move to top
    if (artistIndex > 0) {
      const [artistToBoost] = combinedResults.splice(artistIndex, 1);
      combinedResults.unshift(artistToBoost);
    }
  }

  return (
    <Flex direction="column" gap={2}>
      {combinedResults.length === 0 && <BrowseEmptyState />}
      {combinedResults.map((result, i) => (
        <SearchResultCard
          key={`${result.type}-${result.url}`}
          result={result}
          index={i}
          searchTerm={searchTerm}
          onNavigate={onNavigate}
          showDivider={i < combinedResults.length - 1}
        />
      ))}
    </Flex>
  );
}

function SearchResultCard({
  result,
  index,
  searchTerm,
  onNavigate,
  showDivider,
}: {
  result: ResultDisplay;
  index: number;
  searchTerm: string;
  onNavigate?: () => void;
  showDivider: boolean;
}) {
  const navigate = useNavigate();
  const { openComicInfoMenu, openArtistProfile } = useSlideInMenu();

  const { goToCTAUrl } = useNavigateToReaderWithContext({
    releaseToNavigate:
      result.type === "Release" ? (result.entity as Release) : undefined,
    trigger: NavigationTrigger.searchResultCard,
    area: "search",
    cardPosition: index,
  });

  const handleClick = (e: React.MouseEvent) => {
    const navigationContext = {
      area: "search",
      trigger: NavigationTrigger.searchResultCard,
      cardPosition: index,
      q: searchTerm,
    } as const;

    switch (result.type) {
      case "Artist":
        e.preventDefault();
        openArtistProfile((result.entity as Artist).id, navigationContext);
        onNavigate?.();
        break;
      case "Comic":
        e.preventDefault();
        openComicInfoMenu(
          (result.entity as Comic).slug,
          undefined,
          navigationContext,
        );
        onNavigate?.();
        break;
      case "Release":
        e.preventDefault();
        goToCTAUrl();
        onNavigate?.();
        break;
      case "News":
        navigate(new URL((result.entity as Article).url).pathname);
        onNavigate?.();
        break;
    }
  };

  return (
    <Box
      cursor="pointer"
      onClick={handleClick}
      style={{
        height: "100%",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        flexDirection: "column",
        gap: "4px",
      }}
    >
      <Flex
        padding={2}
        direction="row"
        gap={2}
        height="100%"
        alignItems="center"
      >
        <Image
          loading="lazy"
          src={result.image}
          alt={result.title}
          h={10}
          w="auto"
        />
        {transformTitle(
          result.title,
          result.type,
          searchTerm,
          result.entity?.artist_roman_name,
        )}
      </Flex>
      {showDivider && <Box minHeight="1px" w="full" background="neutral.800" />}
    </Box>
  );
}

function transformTitle(
  title: string,
  type: string,
  termMatch: string,
  artistName?: string,
): React.ReactNode {
  const matches = title.split(new RegExp(`(${termMatch})`, "gi"));

  return (
    <>
      <Text variant="navbar">
        {matches.map((match, i) => {
          const matcher = new RegExp(`(${termMatch})`, "gi");

          return (
            <Text
              key={match + "-" + i}
              as="span"
              fontWeight="bold"
              color={matcher.test(match) ? "dune.100" : "charcoal.200"}
            >
              {match}
            </Text>
          );
        })}
      </Text>
      <Flex
        alignItems="center"
        background="transparent.white.10"
        padding="5px 8px"
        borderRadius="45px"
        height="21px"
        whiteSpace="none"
      >
        <Text
          variant="navbar"
          color="dune.100"
          fontSize="sm"
          letterSpacing=".25px"
          whiteSpace="nowrap"
        >
          In {type}
        </Text>
      </Flex>
      {artistName && (
        <Text variant="navbar" color="charcoal.200">
          {artistName}
        </Text>
      )}
    </>
  );
}
